import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeCtx = createContext({
  theme: 'system',         // user's PREFERENCE — 'light' | 'dark' | 'system'
  resolvedTheme: 'light',  // actual applied theme after resolving 'system'
  setTheme: () => {},
});

const STORAGE_KEY = 'gm_theme';

const getSystemTheme = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const applyToDocument = (resolved) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
    root.setAttribute('data-theme', 'dark');
  } else {
    root.classList.remove('dark');
    root.setAttribute('data-theme', 'light');
  }
  // Update <meta name="theme-color"> so the mobile status bar follows the theme.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0F172A' : '#FFFFFF');
};

export function ThemeProvider({ children }) {
  // Initial value: read from localStorage so the choice survives reloads.
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    return localStorage.getItem(STORAGE_KEY) || 'system';
  });

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  // Apply the class on every change.
  useEffect(() => {
    applyToDocument(resolvedTheme);
  }, [resolvedTheme]);

  // When user picked 'system', listen for OS-level changes and react live.
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyToDocument(getSystemTheme());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!['light', 'dark', 'system'].includes(next)) return;
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
